import { Dispatch, SetStateAction, useMemo } from "react";
import {
  GetAllProductOptionsQuery,
  Product,
} from "../../../graphql/operations";
import SearchCard, {
  SearchCardProps,
} from "../MultiCardModal/components/SearchCard";

type ProductOption = GetAllProductOptionsQuery["products"]["records"][number];

export type ProductCardProps = Omit<SearchCardProps, "label"> & {
  productId?: Product["id"];
  setProductId: Dispatch<SetStateAction<Product["id"]>>;
  productOptions: ProductOption[];
  disabled?: boolean;
};

export default function ProductCard({
  productId,
  setProductId,
  productOptions,
  disabled,
  ...searchCardProps
}: ProductCardProps) {
  const sortedProductOptionsPerLocation = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(
          productOptions.reduce((acc, option) => {
            const locationName = option.location.name;

            if (!Object.prototype.hasOwnProperty.call(acc, locationName))
              acc[locationName] = [];
            acc[locationName].push(option);
            return acc;
          }, {} as Record<string, ProductOption[]>)
        ).map(([locationName, options]) => [
          locationName,
          options.sort((a, b) =>
            a.translation.name.localeCompare(b.translation.name)
          ),
        ])
      ),
    [productOptions]
  );

  return (
    <SearchCard
      {...searchCardProps}
      label={
        productOptions.find((option) => option.id === productId)?.translation
          .name ?? ""
      }
    >
      <select
        style={{
          width: "100%",
          padding: "0.5rem",
          borderRadius: 8,
        }}
        onChange={(e) => {
          setProductId(e.target.value);
          searchCardProps.expandNextCard();
        }}
      >
        {Object.entries(sortedProductOptionsPerLocation).flatMap(
          ([locationName, productOptions]) => (
            <>
              <option key={locationName} value={locationName} disabled>
                {locationName}
              </option>
              {productOptions.map(({ id, translation }) => (
                <option key={id} value={id} selected={id === productId}>
                  {translation.name}
                </option>
              ))}
            </>
          )
        )}
      </select>
    </SearchCard>
  );
}
